
import { useAuth } from '../../../hooks/useAuth';
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  onRefresh: () => void;
  isConnected: boolean;
}

export default function Header({ onRefresh, isConnected }: HeaderProps) {
  const { user, logout, uploadProfilePhoto } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogout = async () => {
    try {
      console.log('Logout button clicked');
      setShowUserMenu(false); // Close dropdown if open
      await logout();
      console.log('Logout successful, navigating to login');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  const handleRefresh = () => {
    onRefresh();
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const success = await uploadProfilePhoto(file);
      if (success) {
        alert('Profile photo updated successfully!');
      } else {
        alert('Failed to upload profile photo. Please try again.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('An error occurred while uploading. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getProfilePhotoUrl = () => {
    if (user?.profile_photo_url) {
      return user.profile_photo_url;
    }
    
    // Generate avatar based on email using a service like Gravatar or UI Avatars
    const email = user?.email || '';
    const initials = email.split('@')[0].substring(0, 2).toUpperCase();
    
    // Using UI Avatars service as fallback
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3b82f6&color=ffffff&size=128`;
  };

  const handleReportIssue = () => {
    const subject = encodeURIComponent('Website/App Issue Report');
    const body = encodeURIComponent(`Hello Carlos,

I am reporting an issue with the Driven Insurance dashboard:

Issue Description:
[Please describe the issue you encountered]

Browser: ${navigator.userAgent}
Date/Time: ${new Date().toLocaleString()}
User: ${user?.email || 'Unknown'}

Please let me know if you need any additional information.

Best regards`);
    
    const mailtoLink = `mailto:carlos@driveninsurance.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  return (
    <>
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="border-b border-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* First row - Company Logo centered */}
            <div className="flex justify-center items-center h-16 border-b border-gray-100">
              <div className="flex-shrink-0">
                <img 
                  src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/754206e4eaed9e298ae32598b22b9529.png"
                  alt="Driven Insurance Services"
                  className="h-14 w-auto object-contain"
                />
              </div>
            </div>

            {/* Second row - Controls under the banner */}
            <div className="flex justify-between items-center h-12">
              {/* Left side - Refresh button and Warning button */}
              <div className="flex items-center space-x-3">
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  className="flex items-center justify-center w-8 h-8 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  title="Refresh"
                >
                  <i className="ri-refresh-line"></i>
                </button>

                {/* Report Issue Button - moved further left with larger margin */}
                <button
                  onClick={handleReportIssue}
                  className="flex items-center justify-center w-8 h-8 text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors shadow-md mr-8"
                  title="Report Website/App Issue"
                >
                  <i className="ri-error-warning-line"></i>
                </button>
              </div>

              {/* Right side - User Email, Profile and Logout */}
              <div className="flex items-center space-x-3">
                {/* User Email */}
                <div className="text-xs text-gray-7 00">
                  <span className="font-medium">{user?.email}</span>
                </div>

                {/* User Profile */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
                    disabled={isUploading}
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-gray-200 hover:border-blue-303 transition-colors">
                      {isUploading ? (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin text-blue-500 text-sm"></i>
                        </div>
                      ) : (
                        <img 
                          src={getProfilePhotoUrl()}
                          alt="Profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to icon if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.parentElement!.innerHTML = '<i class="ri-user-line text-white text-sm bg-blue-500 w-full h-full flex items-center justify-center"></i>';
                          }}
                        />
                      )}
                    </div>
                  </button>

                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                        <p className="text-xs text-gray-500">Administrator</p>
                      </div>
                      
                      {/* Profile Photo Upload */}
                      <div className="px-4 py-2 border-b border-gray-100">
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="w-full text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center py-1 rounded disabled:opacity-50"
                        >
                          <i className="ri-camera-line mr-2"></i>
                          {isUploading ? 'Uploading...' : 'Change Photo'}
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </div>

                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <i className="ri-logout-circle-line mr-2"></i>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 bg-gray-100 rounded hover:bg-red-100 hover:text-red-600 transition-colors"
                  title="Sign Out"
                >
                  <i className="ri-logout-circle-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Logo Row Section - Now sticky */}
      <div className="bg-gray-200 border-b border-gray-200 shadow-sm sticky top-[112px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-3 space-x-4">
            <a
              href="https://us02web.zoom.us/j/8048589226?pwd=NkxoaHJvWWhYOTFSUFV0ZDczeDVwZz09"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/fcf092511ee846fcf915342a998d5b39.png"
                alt="Zoom Meeting"
                className="w-8 h-8 object-contain"
              />
            </a>
            
            <a
              href="https://www.instagram.com/driven_insurance/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/1a9ca2dce5bb60fdff3f672619fa5ad5.png"
                alt="Instagram"
                className="w-8 h-8 object-contain"
              />
            </a>

            <a
              href="https://www.tiktok.com/@driveninsurance"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/378d8d4692e71cd47c762d8e1d627658.png"
                alt="TikTok"
                className="w-8 h-8 object-contain"
              />
            </a>

            <a
              href="https://www.facebook.com/profile.php?id=61577703207742"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/ef312040f9a9581b144c4d6cb67c52cc.png"
                alt="Facebook"
                className="w-8 h-8 object-contain"
              />
            </a>

            <a
              href="https://www.google.com/search?sca_esv=e211aab3cf833b15&rlz=1C1VDKB_enUS934US934&sxsrf=AE3TifP1Jmi58CGTl_ynhv8-dcRcyf5wKA:1760102087659&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E9bsg8y1eFAvB-rq-e_v05W2jMlOw5vimLmAmf2WZLv0oWezv4ABj1LNJG1Fl_bhUlqXHWCucAth5SfmnMii49kH_RUX6aoUpCfY0xo8HKgaMMxnTA%3D%3D&q=Driven+Insurance+Reviews&sa=X&ved=2ahUKEwjGgNCd25mQAxW2JkQIHeGEBf8Q0bkNegQIPBAE&biw=1898&bih=918&dpr=1"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/70220420658129de83a1c30ca2ee971b.png"
                alt="Google Reviews"
                className="w-8 h-8 object-contain"
              />
            </a>

            <a
              href="https://agents.agencymatrix.com/index.php"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img 
                src="https://static.readdy.ai/image/179d4a3273ef4ee52c79cd3980b80e29/885aa574b0bdb2509cdb588443b854a5.png"
                alt="Agency Matrix"
                className="w-8 h-8 object-contain"
              />
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
